import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import RequireAuth from "./components/auth/RequireAuth";
import RequireRole from "./components/auth/RequireRole";
import NotFound from "./components/shared/NotFoundPage";
import FacilityDeactivatedPage from "./components/shared/FacilityDeactivatedPage";
import RouteProgressBar from "./components/shared/RouteProgressBar";
import RouteLazyErrorBoundary from "./components/shared/RouteLazyErrorBoundary";
import FacilityLayout from "./layouts/FacilityLayout";
import { routeImports } from "./routeImports";

const FacilityOverview = lazy(routeImports.facilityOverview);
const SystemStatus = lazy(routeImports.systemStatus);
const EnergyAnalysis = lazy(routeImports.energyAnalysis);
const AIInsights = lazy(routeImports.aiInsights);
const PerformanceAnalysis = lazy(routeImports.performanceAnalysis);
const AddOrganizationPage = lazy(routeImports.addOrganization);
const AdminDashboard = lazy(routeImports.adminDashboard);
const AcceptInvitePage = lazy(routeImports.acceptInvite);
const SignInPage = lazy(routeImports.signIn);
const ForgotPasswordPage = lazy(routeImports.forgotPassword);
const ResetPasswordPage = lazy(routeImports.resetPassword);
const ProfilePage = lazy(routeImports.profile);
const ManageAdminsPage = lazy(routeImports.manageAdmins);
const FacilitySummaryPage = lazy(routeImports.facilitySummary);
const TelemetryOutagePage = lazy(routeImports.telemetryOutage);
const ScenarioSimulator = lazy(routeImports.scenarioSimulator);
const CustomDashboardsListPage = lazy(routeImports.customDashboardsList);

const CustomDashboardBuilderPage = lazy(() =>
  routeImports.customDashboardRoutePages().then((m) => ({ default: m.CustomDashboardBuilderPage })),
);
const NewDashboardBuilderPage = lazy(() =>
  routeImports.customDashboardRoutePages().then((m) => ({ default: m.NewDashboardBuilderPage })),
);
const CustomDashboardViewPage = lazy(() =>
  routeImports.customDashboardRoutePages().then((m) => ({ default: m.CustomDashboardViewPage })),
);

export default function RoutesComponent() {
  return (
    <RouteLazyErrorBoundary>
      <Suspense fallback={<RouteProgressBar />}>
        <Routes>

          {/* ── Guest-only ────────────────────────────────────────────────────── */}
          <Route path="/sign-in" element={<SignInPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/invite/accept" element={<AcceptInvitePage />} />

          {/* ── Platform admin pages ──────────────────────────────────────────── */}
          <Route path="/admin/dashboard"
            element={<RequireAuth><RequireRole page="admin_dashboard"><AdminDashboard /></RequireRole></RequireAuth>}
          />
          <Route path="/manage-admins"
            element={<RequireAuth><RequireRole page="manage_admins"><ManageAdminsPage /></RequireRole></RequireAuth>}
          />
          {/* Phase 2 — Permissions Manager: will be introduced in the next release */}
          {/* <Route path="/permissions"
            element={<RequireAuth><RequireRole page="permissions_manager"><NotFound /></RequireRole></RequireAuth>}
          /> */}
          <Route path="/new-organization"
            element={<RequireAuth><RequireRole page="new_organization"><AddOrganizationPage /></RequireRole></RequireAuth>}
          />

          {/* ── Authenticated — facility summary ────────────────────────────────────── */}
          <Route path="/organizations/facility/:facilityId/summary"
            element={<RequireAuth><RequireRole page="facility_summary"><FacilitySummaryPage /></RequireRole></RequireAuth>}
          />
          <Route path="/organizations/facility/:facilityId/telemetry-outage"
            element={<RequireAuth><RequireRole page="telemetry_outage"><TelemetryOutagePage /></RequireRole></RequireAuth>}
          />

          {/* ── Profile — all authenticated users ────────────────────────────── */}
          <Route path="/profile"
            element={<RequireAuth><RequireRole page="profile"><ProfilePage /></RequireRole></RequireAuth>}
          />

          {/* Landing page for a signed-in user with no active facility to land
              on — see SignInForm's post-login redirect resolution. Renders
              through the normal AppLayout (sidebar + header both stay —
              AppSidebar shows its own "no active facility" message under
              Organizations rather than an empty list). */}
          <Route path="/facility-deactivated"
            element={<RequireAuth><RequireRole page="facility_deactivated"><FacilityDeactivatedPage message="Contact organization admin for more details." /></RequireRole></RequireAuth>}
          />

          {/* ── Facility scoped — org/facility roles ──────────────────────────── */}
          {/* RequireAuth/RequireRole/RequireFacility all gate once here, at the
              parent level, instead of being repeated on every child below —
              and in that order: auth and role must resolve before anything
              reads facility/org state, since RequireFacility assumes both
              already passed. */}
          <Route
            path="/organizations/:orgId/facilities/:facilityId"
            element={
              <RequireAuth>
                <RequireRole page="facility_pages">
                  <FacilityLayout />
                </RequireRole>
              </RequireAuth>
            }
          >
            <Route path="overview" element={<FacilityOverview />} />
            <Route path="ai-insights" element={<AIInsights />} />
            <Route path="scenario-simulator" element={<ScenarioSimulator />} />
            <Route path="performance-analysis" element={<PerformanceAnalysis />} />
            <Route path="energy-analysis" element={<EnergyAnalysis />} />
            <Route path="system-status" element={<SystemStatus />} />
            <Route path="chatbot" element={<NotFound />} />
            <Route path="custom-dashboards" element={<CustomDashboardsListPage />} />
            <Route path="custom-dashboards/new" element={<NewDashboardBuilderPage />} />
            <Route path="custom-dashboards/:dashboardId" element={<CustomDashboardViewPage />} />
            <Route path="custom-dashboards/:dashboardId/edit" element={<CustomDashboardBuilderPage />} />
          </Route>

          {/* ── Catch-all ─────────────────────────────────────────────────────── */}
          <Route path="*" element={<NotFound />} />

        </Routes>
      </Suspense>
    </RouteLazyErrorBoundary>
  );
}
